import { invoke } from '@tauri-apps/api/core';

let connectionKey = '';
let providersCache = null;
let entitiesCache = null;
let serviceActionsCache = null;
let targetServicesCache = new Map();
const voicesCache = new Map();
const TTS_DEFAULTS_KEY = 'openhasp-designer.tts-defaults';

export function getPreferredTtsDefaults() {
  try {
    return JSON.parse(localStorage.getItem(TTS_DEFAULTS_KEY) ?? 'null');
  } catch {
    return null;
  }
}

export function savePreferredTtsDefaults({ provider, language, voice }) {
  if (!provider || !language || !voice) return;
  try {
    localStorage.setItem(TTS_DEFAULTS_KEY, JSON.stringify({ provider, language, voice }));
  } catch {
    // Storage can be unavailable in restricted webviews; action persistence
    // remains unaffected in that case.
  }
}

function keyFor(settings) {
  const hass = settings?.hass ?? {};
  return `${hass.protocol}://${hass.host}:${hass.port}|${hass.token ?? ''}`;
}

function connectionArgs(settings) {
  const hass = settings.hass;
  return {
    hassurl: `${hass.protocol}://${hass.host}:${hass.port}`,
    token: hass.token,
  };
}

function ensureConnection(settings) {
  const nextKey = keyFor(settings);
  if (nextKey !== connectionKey) {
    connectionKey = nextKey;
    providersCache = null;
    entitiesCache = null;
    serviceActionsCache = null;
    targetServicesCache = new Map();
    voicesCache.clear();
  }
}

export async function fetchEntities(settings, { refresh = false } = {}) {
  ensureConnection(settings);
  if (refresh) entitiesCache = null;
  if (!entitiesCache) {
    entitiesCache = invoke('hass_fetch_entities_command', connectionArgs(settings))
      .catch(error => {
        entitiesCache = null;
        throw error;
      });
  }
  return entitiesCache;
}

function attachServiceMetadata(option, metadata) {
  // Keep the picker option's public shape stable for existing consumers while
  // making the complete HA schema available to the dynamic field editor.
  // Non-enumerable properties also keep old persisted/test-facing option
  // snapshots compatible with the pre-schema picker.
  const fields = metadata?.fields && typeof metadata.fields === 'object' && !Array.isArray(metadata.fields)
    ? metadata.fields
    : {};
  const target = metadata?.target && typeof metadata.target === 'object' && !Array.isArray(metadata.target)
    ? metadata.target
    : null;
  Object.defineProperty(option, 'fields', { value: fields, enumerable: false, configurable: true });
  Object.defineProperty(option, 'target', { value: target, enumerable: false, configurable: true });
  return option;
}

/** Map Home Assistant's registered service metadata to full domain.service picker options. */
export function serviceActionOptions(catalog) {
  const entries = Array.isArray(catalog)
    ? catalog
    : catalog && typeof catalog === 'object'
      ? Object.entries(catalog).map(([domain, services]) => ({ domain, services }))
      : [];
  return entries.flatMap(({ domain, services } = {}) => {
    if (typeof domain !== 'string' || !domain || !services || typeof services !== 'object' || Array.isArray(services)) {
      return [];
    }
    return Object.entries(services).filter(([service]) => Boolean(service)).map(([service, metadata]) => {
      const value = `${domain}.${service}`;
      const serviceMetadata = metadata && typeof metadata === 'object' && !Array.isArray(metadata)
        ? metadata
        : {};
      return attachServiceMetadata({
        value,
        label: value,
        domain,
        service,
        name: typeof serviceMetadata.name === 'string' ? serviceMetadata.name : '',
        description: typeof serviceMetadata.description === 'string' ? serviceMetadata.description : '',
      }, serviceMetadata);
    });
  }).sort((left, right) => left.value.localeCompare(right.value));
}

/** Fetch the complete registered service catalog through the existing Tauri/HA connection. */
export async function getHomeAssistantServiceActions(settings, { refresh = false } = {}) {
  if (!settings?.tauri) {
    throw new Error('Home Assistant service actions require the desktop connection.');
  }
  ensureConnection(settings);
  if (refresh) serviceActionsCache = null;
  if (!serviceActionsCache) {
    serviceActionsCache = invoke('hass_fetch_domain_services_command', {
      ...connectionArgs(settings),
    }).then(serviceActionOptions).catch(error => {
      serviceActionsCache = null;
      throw error;
    });
  }
  return serviceActionsCache;
}

/** Fetch HA's server-filtered action identifiers for the selected entity target. */
export async function getHomeAssistantServicesForTarget(settings, entityIds, { refresh = false } = {}) {
  if (!settings?.tauri) {
    throw new Error('Home Assistant target actions require the desktop connection.');
  }
  ensureConnection(settings);
  const targets = [...new Set((Array.isArray(entityIds) ? entityIds : [entityIds])
    .filter(id => typeof id === 'string' && id.trim()))];
  if (!targets.length) return [];

  const cacheKey = JSON.stringify([...targets].sort());
  if (refresh) targetServicesCache.delete(cacheKey);
  if (!targetServicesCache.has(cacheKey)) {
    const request = invoke('hass_get_services_for_target_command', {
      ...connectionArgs(settings),
      target: { entity_id: targets },
    }).then(result => Array.isArray(result)
      ? [...new Set(result.filter(service => typeof service === 'string' && service.includes('.')))]
      : [])
      .catch(error => {
        targetServicesCache.delete(cacheKey);
        throw error;
      });
    targetServicesCache.set(cacheKey, request);
  }
  return targetServicesCache.get(cacheKey);
}

/** Join HA's applicable identifiers with cached REST metadata, keeping unknown IDs usable. */
export function serviceActionsForTarget(serviceIds, catalog) {
  if (!Array.isArray(serviceIds)) return [];
  const metadataById = new Map((Array.isArray(catalog) ? catalog : []).map(option => [option.value, option]));
  return [...new Set(serviceIds.filter(service => typeof service === 'string' && service.includes('.')))]
    .map(value => metadataById.get(value) ?? {
      value,
      label: value,
      domain: value.slice(0, value.indexOf('.')),
      service: value.slice(value.indexOf('.') + 1),
      name: '',
      description: '',
    })
    .sort((left, right) => left.value.localeCompare(right.value));
}

export async function getEntityInfo(settings, entityId) {
  const entities = await fetchEntities(settings);
  return entities.find(entity => entity.entity_id === entityId) ?? null;
}

export async function getTtsProviders(settings, { refresh = false } = {}) {
  ensureConnection(settings);
  if (refresh) {
    providersCache = null;
    voicesCache.clear();
  }
  if (!providersCache) {
    providersCache = invoke('hass_get_tts_providers_command', connectionArgs(settings))
      .catch(error => {
        providersCache = null;
        voicesCache.clear();
        throw error;
      });
  }
  return providersCache;
}

export async function getTtsVoices(settings, providerEntityId, language, { refresh = false } = {}) {
  ensureConnection(settings);
  const cacheKey = `${providerEntityId}|${language}`;
  if (refresh) voicesCache.delete(cacheKey);
  if (!voicesCache.has(cacheKey)) {
    const request = invoke('hass_get_tts_voices_command', {
      ...connectionArgs(settings),
      engineId: providerEntityId,
      language,
    }).catch(error => {
      voicesCache.delete(cacheKey);
      throw error;
    });
    voicesCache.set(cacheKey, request);
  }
  return voicesCache.get(cacheKey);
}

export async function browseMedia(settings, entityId, {
  mediaContentId = null,
  mediaContentType = null,
} = {}) {
  if (!settings?.tauri) {
    throw new Error('Home Assistant media browsing requires the desktop connection.');
  }
  const result = await invoke('hass_browse_media_command', {
    ...connectionArgs(settings),
    entityId,
    mediaContentId,
    mediaContentType,
  });
  return result ?? {};
}

/**
 * Reload YAML automations through Home Assistant's automation service.
 * The generic desktop service command currently accepts the empty entity ID
 * for services, such as automation.reload, that have no entity target.
 */
export async function reloadHomeAssistantAutomations(settings) {
  if (!settings?.tauri) {
    throw new Error('Reloading Home Assistant automations requires the desktop connection.');
  }

  console.info('Reloading automations');
  try {
    const result = await invoke('hass_call_service_command', {
      ...connectionArgs(settings),
      domain: 'automation',
      service: 'reload',
      entityId: '',
    });
    console.info('Automations reloaded successfully');
    return result;
  } catch (error) {
    console.error('Home Assistant automations reload failed:', error);
    throw error;
  }
}

/** Reload the openHASP integration after uploading a plate YAML file. */
export async function reloadOpenHasp(settings) {
  if (!settings?.tauri) {
    throw new Error('Reloading openHASP requires the desktop connection.');
  }

  const result = await invoke('hass_reload_openhasp_command', {
    ...connectionArgs(settings),
  });
  return result;
}

function encodeUtf8Base64(value) {
  const bytes = new TextEncoder().encode(String(value ?? ''));
  return btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join(''));
}

/** Upload the Designer-managed HA YAML file and reload automations afterwards. */
export async function deployHomeAssistantAutomationYaml(settings, filename, yamlText) {
  const hass = settings?.hass ?? {};
  const connArgs = {
    host: hass.host,
    port: Number(hass.sftp_port),
    user: hass.sftp_user,
    password: hass.sftp_pass,
    timeout: 15,
  };

  await invoke('sftp_upload_file_command', {
    connArgs,
    remotePath: hass.sftp_path,
    filename,
    base64data: encodeUtf8Base64(yamlText),
  });
  console.info('HA automation YAML uploaded');

  try {
    await reloadHomeAssistantAutomations(settings);
  } catch (error) {
    // Preserve the original HA error while allowing the UI to distinguish a
    // successful upload from a failed reload.
    const reloadError = error instanceof Error ? error : new Error(String(error));
    reloadError.uploaded = true;
    reloadError.cause = error;
    throw reloadError;
  }

  return { filename };
}

/** Upload native openHASP plate YAML and reload the openHASP integration. */
export async function deployOpenHaspPlateYaml(settings, filename, yamlText) {
  const hass = settings?.hass ?? {};
  const connArgs = {
    host: hass.host,
    port: Number(hass.sftp_port),
    user: hass.sftp_user,
    password: hass.sftp_pass,
    timeout: 15,
  };

  await invoke('sftp_upload_file_command', {
    connArgs,
    remotePath: hass.sftp_path,
    filename,
    base64data: encodeUtf8Base64(yamlText),
  });

  try {
    const reloaded = await reloadOpenHasp(settings);
    return { filename, reloaded };
  } catch (error) {
    const reloadError = error instanceof Error ? error : new Error(String(error));
    reloadError.uploaded = true;
    reloadError.cause = error;
    throw reloadError;
  }
}

export function invalidateHomeAssistantCache() {
  providersCache = null;
  entitiesCache = null;
  voicesCache.clear();
}
